import { io, Socket } from "socket.io-client";
import { supabase } from "./supabase";
import { offlineSyncService } from "./offlineSync";
import config from "../config";

const SERVER_URL = config.API_URL || window.location.origin;

// Stable request-id per browser tab. The server reads this from
// `socket.handshake.auth.requestId` (see server/index-physical100.js) and
// writes it onto `socket.data.requestId` so downstream event handlers can
// correlate Socket.io traffic with HTTP requests. Tab-scoped (not per-emit)
// keeps the correlation meaningful across a single WS session.
const TAB_REQUEST_ID =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export type ConnectionStatus = "online" | "degraded" | "offline" | "reconnecting";

class SocketClient {
  private socket: Socket;
  private _connectionCallbacks: Array<(connected: boolean) => void> = [];
  private _status: ConnectionStatus = "offline";
  private _heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private _networkOnline = true;
  private _isConnecting = false;
  // Gate that blocks offline-queue replay until select-project has been
  // (re)acked on the current connection — see selectProject/waitForProjectReady.
  private _projectReady: Promise<void> = Promise.resolve();
  private _resolveProjectReady: (() => void) | null = null;
  private _activeProjectId: string | null = null;

  constructor() {
    this.socket = io(SERVER_URL, {
      autoConnect: false, // Don't connect until we have a token
      transports: ["websocket"], // Bypass HTTP long-polling (fixes Railway sticky sessions issue)
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      // Seed handshake auth so the server sees `requestId` even before the
      // first refreshAuth() call completes. `auth` is merged (not replaced)
      // when we later assign `this.socket.auth = { token }`.
      auth: { requestId: TAB_REQUEST_ID },
    });

    // Inject auth token before each connection attempt
    this.socket.on("connect_error", async (err) => {
      if (
        err.message === "Authentication required" ||
        err.message === "Invalid or expired session"
      ) {
        console.warn("[SocketClient] Auth error, refreshing token...");
        await this.refreshAuth();
      }
    });

    this.setupDebugListeners();

    // Proactively rehydrate socket.auth when Supabase rotates the access_token
    // in background. Without this we only learn about the new token reactively
    // (on connect_error), which leaves a window where reconnect attempts use
    // the stale token and the server rejects them with "Invalid or expired
    // session".
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED" && session?.access_token) {
        this.socket.auth = { token: session.access_token, requestId: TAB_REQUEST_ID };
        if (!this.socket.connected) {
          this.socket.connect();
        }
      }
    });

    // Initialize offline sync with this socket instance
    offlineSyncService.init(this);

    // Native browser online/offline detection (catches real network drops)
    window.addEventListener("offline", () => {
      console.warn("[SocketClient] Browser went OFFLINE (network)");
      this._status = "offline";
      this.notifyConnectionChange(false);
    });
    window.addEventListener("online", () => {
      console.debug("[SocketClient] Browser came ONLINE (network)");
      if (this.socket.connected) {
        this._status = "online";
        this.notifyConnectionChange(true);
      } else {
        this._status = "reconnecting";
        // Force reconnect
        this.connect();
      }
    });
  }

  private async refreshAuth() {
    try {
      let {
        data: { session },
      } = await supabase.auth.getSession();
      // getSession() returns the cached token even when expired. After a long
      // tab sleep the access_token can be stale, so force a refresh when it's
      // within 30s of expiry — otherwise the server keeps rejecting reconnects
      // with "Invalid or expired session" in a loop.
      const expMs = (session?.expires_at ?? 0) * 1000;
      if (!session || expMs - Date.now() < 30_000) {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          console.warn("[SocketClient] refreshSession failed:", error.message);
        }
        session = data?.session ?? session;
      }
      if (session?.access_token) {
        // Preserve requestId across token refreshes (socket.auth is replaced
        // here, not merged by socket.io — we rehydrate it explicitly).
        this.socket.auth = { token: session.access_token, requestId: TAB_REQUEST_ID };
      }
    } catch (e) {
      console.error("[SocketClient] Failed to refresh auth:", e);
    }
  }

  public async connect() {
    if (this.socket.connected || this._isConnecting) return;
    this._isConnecting = true;
    try {
      // Inject fresh auth token before connecting
      await this.refreshAuth();
      this.socket.connect();
    } finally {
      this._isConnecting = false;
    }
    this.startHeartbeat();
  }

  public async reconnect() {
    console.debug("[SocketClient] Manual reconnect triggered");
    this.disconnect();
    // Brief delay to ensure clean teardown before bringing it back up
    setTimeout(async () => {
      await this.connect();
    }, 500);
  }

  private startHeartbeat() {
    if (this._heartbeatInterval) return;
    this._heartbeatInterval = setInterval(async () => {
      try {
        // Ping external URL — if this fails, network is truly down
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);
        await fetch("https://www.google.com/favicon.ico", {
          method: "HEAD",
          mode: "no-cors",
          signal: controller.signal,
          cache: "no-store",
        });
        clearTimeout(timeout);

        if (!this._networkOnline) {
          this._networkOnline = true;
          console.debug("[SocketClient] ❤️ Heartbeat OK — network restored");
          this._status = this.socket.connected ? "online" : "reconnecting";
          this.notifyConnectionChange(this.socket.connected);
        }
      } catch {
        if (this._networkOnline) {
          this._networkOnline = false;
          console.warn("[SocketClient] 💀 Heartbeat FAILED — network down");
          // Socket may still be connected locally while external net is down:
          // surface this as "degraded" rather than full offline so the UI
          // distinguishes "everything broken" from "we can talk to the server
          // but not the outside world".
          this._status = this.socket.connected ? "degraded" : "offline";
          this.notifyConnectionChange(this.socket.connected);
        }
      }
    }, 3000);
  }

  public disconnect() {
    if (this.socket.connected) {
      this.socket.disconnect();
    }
  }

  public get connected(): boolean {
    return this.socket.connected;
  }

  public get connectionStatus(): ConnectionStatus {
    // Always trust the actual socket state over tracked _status, but when
    // the socket is connected still surface "degraded" when the external
    // network heartbeat is failing.
    if (this.socket.connected) {
      return this._networkOnline ? "online" : "degraded";
    }
    return this._status === "reconnecting" ? "reconnecting" : "offline";
  }

  /**
   * Emit "select-project" with a server ack. The ack resolves the project-ready
   * gate so the offline queue only replays after the server has re-established
   * `socket.projectId` for this connection. On a fresh reconnect the server
   * socket is brand new (projectId lost), so replaying update-stories before
   * select-project is processed gets rejected with "No projectId (undefined)".
   */
  public selectProject(projectId: string, options?: unknown) {
    this._activeProjectId = projectId;
    // When disconnected we don't emit; ProjectContext re-selects on "connect".
    if (!this.socket.connected) return;
    const ack = () => this._resolveProjectReady?.();
    if (options !== undefined) {
      this.socket.emit("select-project", projectId, options, ack);
    } else {
      this.socket.emit("select-project", projectId, ack);
    }
  }

  /** Arm a fresh pending gate on (re)connect; resolved by the select-project ack. */
  private armProjectGate() {
    this._projectReady = new Promise<void>((resolve) => {
      this._resolveProjectReady = resolve;
    });
  }

  /**
   * Awaited by offlineSync before replaying queued ops. Races a timeout so a
   * missing/old server ack degrades gracefully instead of stalling the queue.
   */
  public waitForProjectReady(timeoutMs = 8000): Promise<void> {
    return Promise.race([
      this._projectReady,
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  /** Subscribe to connection changes — used by LogBuffer */
  public onConnectionChange(callback: (connected: boolean) => void): () => void {
    this._connectionCallbacks.push(callback);
    return () => {
      this._connectionCallbacks = this._connectionCallbacks.filter((cb) => cb !== callback);
    };
  }

  /** Emit with server acknowledgement callback */
  public emitWithAck(event: string, data: any, callback: (response: any) => void): void {
    if (this.socket.connected) {
      this.socket.emit(event, data, callback);
    } else {
      callback({ success: false, error: "Not connected" });
    }
  }

  public on(event: string, callback: (...args: any[]) => void) {
    this.socket.on(event, callback);
  }

  public off(event: string, callback?: (...args: any[]) => void) {
    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  /**
   * Fire local event listeners without sending to the server.
   * Used in E2E tests to inject synthetic socket events (e.g. projects-list-updated).
   */
  public emitLocal(event: string, ...args: any[]) {
    // socket.io-client stores listeners with a "$" prefix in _callbacks
    const callbacks = (this.socket as any)._callbacks?.[`$${event}`];
    if (Array.isArray(callbacks)) {
      callbacks.forEach((fn: (...a: any[]) => void) => fn(...args));
    }
  }

  public emit(event: string, ...args: any[]) {
    // DEBUG: Trace story persistence issues
    if (event === "update-stories") {
      const count = Array.isArray(args[0]) ? args[0].length : "?";
      console.debug(
        `🔌 [SOCKET EMIT] update-stories: ${count} stories, connected=${this.socket.connected}`,
      );
    }

    if (this.socket.connected) {
      this.socket.emit(event, ...args);
    } else {
      console.warn(`⚠️ [SOCKET] Not connected!`);

      // Si el evento es crítico (Offline Supported), mandarlo a la cola del localStorage
      if (offlineSyncService.isEventSupported(event)) {
        console.debug(`[OfflineSync] 🔌 Intercepted offline event to queue: ${event}`);
        offlineSyncService.queueOperation(event, args);
      } else {
        console.warn(`[SOCKET] Dropping non-critical event queued: ${event}`);
      }
    }
  }

  // Un raw emit directo para que OfflineSync pueda despachar la cola
  public rawEmit(event: string, ...args: any[]) {
    if (this.socket.connected) {
      this.socket.emit(event, ...args);
    }
  }

  public onConnect(callback: () => void) {
    if (this.socket.connected) {
      callback();
    } else {
      this.socket.once("connect", callback);
    }
  }

  private notifyConnectionChange(connected: boolean): void {
    this._connectionCallbacks.forEach((cb) => cb(connected));
  }

  private setupDebugListeners() {
    this.socket.on("connect", () => {
      console.debug("[SocketClient] Connected to server");
      this._status = "online";
      // Arm BEFORE notifying so offlineSync (driven by notifyConnectionChange)
      // blocks until ProjectContext re-emits select-project and the server acks.
      this.armProjectGate();
      this.notifyConnectionChange(true);
    });

    this.socket.on("disconnect", () => {
      console.debug("[SocketClient] Disconnected from server");
      this._status = "offline";
      this.notifyConnectionChange(false);
    });

    this.socket.on("reconnect_attempt", () => {
      this._status = "reconnecting";
    });

    this.socket.on("connect_error", (err) => {
      console.error("[SocketClient] Connection Error:", err);
      this._status = "reconnecting";
    });
  }
}

export const socketService = new SocketClient();

// E2E only: expose socketService on window so Playwright tests can inject
// socket events (e.g. projects-list-updated) without a real server round-trip.
// Activated when VITE_E2E=true (env var) OR when the e2e localStorage sentinel
// is present (set by loginAs() in testAuth.ts — works even without a Vite restart).
if (
  import.meta.env.VITE_E2E === "true" ||
  (typeof localStorage !== "undefined" && localStorage.getItem("__e2e__") === "1")
) {
  (window as any).__socketService = socketService;
}
